import { useEffect, useRef, useState } from 'react'
import { Image } from 'expo-image'
import { Linking, Text, View } from 'react-native'
import { RefreshCw, ShieldCheck } from '@/components/ui/icons'
import { useTranslation } from 'react-i18next'
import type { MandatoryUpdateGate } from '../../lib/hooks/use-mandatory-update-gate'
import { COLORS, FONTS, RADIUS, SPACING } from '../../lib/brand'
import { useSemanticColors, withAlpha, INTERACTION } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { impact, ImpactStyle, notification, NotificationType } from '../../lib/utils/haptics'
import { nativeStoreSchemeUrl } from '../../lib/utils/store-links'
import { Pressable } from './Pressable'

interface MandatoryUpdateScreenProps {
  gate: MandatoryUpdateGate
}

export function MandatoryUpdateScreen({ gate }: MandatoryUpdateScreenProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  // This screen can be the only thing on the display for an entire launch, so
  // it follows the app theme like every other surface. It used to be a frozen
  // light palette, which meant a full-screen sheet of near-white in dark mode.
  const colors = useSemanticColors(isDark)
  const previousStatus = useRef(gate.status)
  const [storeOpenFailed, setStoreOpenFailed] = useState(false)
  const isStoreRequired = gate.status === 'store-required'
  const isError = gate.status === 'error'
  const canOpenStore = isStoreRequired && Boolean(gate.storeUrl)

  const title = isStoreRequired
    ? t('updates.store_required_title')
    : t('updates.blocked_error_title')
  const body = isStoreRequired ? t('updates.store_required_body') : t('updates.blocked_error_body')
  const actionLabel = canOpenStore ? t('updates.open_store') : t('updates.retry')

  useEffect(() => {
    if (previousStatus.current === gate.status) return
    previousStatus.current = gate.status

    if (isError) {
      notification(NotificationType.Error)
    } else if (isStoreRequired) {
      notification(NotificationType.Warning)
    }
  }, [gate.status, isError, isStoreRequired])

  const handlePrimary = async () => {
    impact(isStoreRequired ? ImpactStyle.Medium : ImpactStyle.Light)
    if (canOpenStore && gate.storeUrl) {
      try {
        await Linking.openURL(gate.storeUrl)
        return
      } catch {
        // El OS se negó a abrir la URL (TAGO-MOBILE-5E). Reintentar con el
        // esquema nativo de la tienda antes de rendirse; si tampoco hay
        // manejador, el aviso de abajo dice qué hacer — esta pantalla puede
        // ser lo ÚNICO que el usuario ve, y el botón no puede quedar en nada.
        const schemeUrl = nativeStoreSchemeUrl(gate.storeUrl)
        if (schemeUrl) {
          try {
            await Linking.openURL(schemeUrl)
            return
          } catch {
            // Sin manejador nativo tampoco: cae al aviso visible.
          }
        }
        setStoreOpenFailed(true)
        void notification(NotificationType.Error)
        return
      }
    }
    gate.retry()
  }

  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background,
        paddingHorizontal: SPACING.xl,
      }}
    >
      <View style={{ width: '100%', maxWidth: 390, alignItems: 'center' }}>
        <Image
          source={require('../../assets/images/logo-stacked.png')}
          style={{ width: 86, height: 86, marginBottom: SPACING.xl }}
          contentFit="contain"
          accessibilityIgnoresInvertColors
        />

        <View
          style={{
            width: 54,
            height: 54,
            borderRadius: 27,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: withAlpha(COLORS.coral.DEFAULT, isDark ? 0.18 : 0.1),
            borderWidth: 1,
            borderColor: withAlpha(COLORS.coral.DEFAULT, 0.16),
            marginBottom: SPACING.lg,
          }}
        >
          {isStoreRequired ? (
            <ShieldCheck size={25} color={COLORS.coral.DEFAULT} strokeWidth={1.8} />
          ) : (
            <RefreshCw size={25} color={COLORS.coral.DEFAULT} strokeWidth={1.8} />
          )}
        </View>

        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 25,
            lineHeight: 32,
            color: colors.textPrimary,
            textAlign: 'center',
            marginBottom: SPACING.sm,
          }}
          accessibilityRole="header"
        >
          {title}
        </Text>
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 15,
            lineHeight: 23,
            color: colors.textSecondary,
            textAlign: 'center',
            marginBottom: SPACING.lg,
          }}
        >
          {body}
        </Text>

        {isStoreRequired && gate.requiredBuildVersion ? (
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 12,
              lineHeight: 18,
              color: colors.textMuted,
              textAlign: 'center',
              marginBottom: SPACING.md,
            }}
          >
            {t('updates.build_requirement', {
              current: gate.currentBuildVersion,
              required: gate.requiredBuildVersion,
            })}
          </Text>
        ) : null}

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={actionLabel}
          onPress={handlePrimary}
          style={({ pressed }) => [
            {
              minHeight: INTERACTION.fieldHeight,
              width: '100%',
              borderRadius: RADIUS.full,
              alignItems: 'center',
              justifyContent: 'center',
              paddingVertical: SPACING.md,
              paddingHorizontal: SPACING.lg,
              backgroundColor: COLORS.coral.dark,
            },
            pressed ? { opacity: 0.86 } : null,
          ]}
        >
          <Text
            // No `lineHeight` with `adjustsFontSizeToFit` — see
            // `components/auth/AuthGradientButton`. This is the button behind
            // the update wall, so an unreadable label here strands the user.
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 16,
              color: COLORS.white,
              textAlign: 'center',
            }}
            numberOfLines={2}
            adjustsFontSizeToFit
          >
            {actionLabel}
          </Text>
        </Pressable>

        {storeOpenFailed ? (
          <Text
            accessibilityRole="alert"
            testID="updates.store-open-failed"
            style={{
              marginTop: SPACING.md,
              fontFamily: FONTS.body,
              fontSize: 13,
              lineHeight: 19,
              color: colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {t('updates.open_store_failed')}
          </Text>
        ) : null}
      </View>
    </View>
  )
}
